import React, { useState, useEffect, useCallback, useContext } from 'react';
import get from 'lodash/get';
import map from 'lodash/map';
import set from 'lodash/set';
import styled from 'styled-components';

import Chip from '@ad-scripts/presentation/components/common/chip';
import FormContext from 'src/presentation/contexts/FormContext';
import { Button, InputField } from 'src/presentation/components/agent_desktop_components';
import { FieldContained } from '@ad-scripts/presentation/components/common/input_field';
import { ButtonTypes } from '@ad-scripts/presentation/components/common/button';
import { HalfRow } from './Layout';

type StringArrayProps = {
  attr: string;
  disabled: boolean;
  prompt: string;
};

export default function CommaSeparatedStringArray({ attr, disabled, prompt }: StringArrayProps) {
  const [input, setInput] = useState('');
  const [vals, setVals] = useState<string[]>([]);
  const { workingState, onBlur } = useContext(FormContext);

  useEffect(() => {
    const state: string = get(workingState, attr, '');
    setVals(state.trim() ? state.split(',') : []);
  }, [workingState, attr]);

  const handleChange = (input: string) => {
    setInput(input);
  };

  const updateWorkingState = useCallback(
    (newState: any) => {
      onBlur && onBlur({ currentState: set({}, attr, newState.join(',')), needToOverrideState: false });
    },
    [attr, onBlur]
  );

  const handleDelete = value => {
    const newVals = vals.filter(item => item !== value);
    setVals(newVals);
    updateWorkingState([...newVals]);
  };

  const onKeyDown = useCallback(
    ev => {
      switch (ev.key) {
        case 'Enter':
          ev.preventDefault();
          if (input) {
            setVals([...vals, input]);
            setInput('');
            updateWorkingState([...vals, input]);
          }
          return false;
      }
      return true;
    },
    [vals, input, updateWorkingState]
  );

  const handleClick = useCallback(
    ev => {
      ev.preventDefault();
      setVals([...vals, input]);
      setInput('');
      updateWorkingState([...vals, input]);
    },
    [vals, input, updateWorkingState]
  );

  return (
    <FieldContained
      label={prompt}
      control={
        <ControlContainer>
          <StyledHalfRow>
            <InputField value={input} disabled={disabled} onChange={handleChange} onKeyDown={onKeyDown} />
            <Button disabled={disabled || !input} type={ButtonTypes.OUTLINE} onClick={handleClick} tabIndex={-1}>
              Add
            </Button>
          </StyledHalfRow>
          <Chips>
            {map(vals, value => (
              <StyledChip
                accent={false}
                disabled={disabled}
                key={value}
                label={value}
                onDelete={() => handleDelete(value)}
                role="listitem"
                value={value}
              />
            ))}
          </Chips>
        </ControlContainer>
      }
    />
  );
}

const ControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledHalfRow = styled(HalfRow)`
  margin-bottom: 16px;
`;

const Chips = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  padding-bottom: 0;
`;

const StyledChip = styled(Chip)`
  margin-right: 8px;
  margin-bottom: 8px;
`;
